/**
 * Inicio - Las dos vistas del banner horizontal.
 *
 *   grande    lienzo de 1920×320 (6:1) → popups/{id}.banner.wide
 *   pequeña   lienzo de  960×240 (4:1) → popups/{id}.banner.wide.small
 *
 * La app enseña la pequeña cuando el banner mide menos de SMALL_BELOW px de
 * ancho (ventana estrecha); el corte lo hace el CSS con una container query
 * (home-dashboard.css, mismo número). Con un solo lienzo de 6:1 el banner se
 * quedaba en una tira diminuta: a 600 px de ancho medía 100 px de alto y el
 * título 12 px.
 *
 * Si el popup no trae la vista pequeña diseñada en el backoffice, se saca de
 * la grande con deriveSmall(): mismos textos y botón, recolocados para un
 * lienzo la mitad de ancho.
 *
 * CSS a medida (banner.wide.css): el backoffice puede añadir CSS a un banner
 * para lo que el editor no da (degradados en el texto, animaciones, bordes…).
 * scopedCss() lo limpia y lo encierra en ese banner con @scope, de modo que
 * no puede tocar nada de fuera. Clases a las que agarrarse (CSS_HOOKS):
 *   .bn-wide / .bn-small      la vista grande / la pequeña
 *   .bn-bg                    la imagen de fondo
 *   .bn-text, .bn-text-1…N    los textos (por orden) · .bn-chip su pastilla
 *   .bn-button                el botón
 * y `:scope` para el banner entero. Tamaños que escalen con el banner:
 * calc(20 * var(--hm-u)) = 20 px del lienzo.
 *
 * HTML a medida (banner.wide.html = { es, en }): marcado propio en una capa
 * `.bn-html` a todo el lienzo, limpiado con cleanHtml() (lista cerrada de
 * etiquetas y atributos). Los nombres que se pueden usar —clases, variables,
 * etiquetas— están en REFERENCE, que es lo que enseña el editor.
 *
 * Fuente única: lo cargan el Inicio (promos.js) y el editor del backoffice
 * (backoffice/src/lib/bannerViews.js lo importa tal cual). Sin DOM.
 */
(function (root) {
  'use strict';

  var WIDE = { width: 1920, height: 320 };
  var SMALL = { width: 960, height: 240 };
  var SMALL_BELOW = 1000;
  // Los tamaños (px del lienzo) se conservan casi enteros al pasar a la
  // pequeña: en un lienzo la mitad de ancho eso ya es un 70 % más grande.
  var SIZE_FACTOR = 0.85;
  // Margen lateral (%) que la pequeña deja libre a los textos pegados a un
  // borde: ahí van las flechas del carrusel, que no encogen con el banner.
  var SIDE_SAFE = 8;

  function num(value, fallback) {
    if (value === '' || value === null || value === undefined) return fallback;
    var n = Number(value);
    return isFinite(n) ? n : fallback;
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function copy(obj) {
    var out = {};
    for (var key in obj) if (Object.prototype.hasOwnProperty.call(obj, key)) out[key] = obj[key];
    return out;
  }

  // Caja de la grande (centro x y ancho, en %) → la misma caja en la pequeña.
  // Para que el texto parta de línea igual necesita el doble de % (el lienzo
  // mide la mitad) por SIZE_FACTOR. Se ancla al lado hacia el que está
  // alineado el contenido, para que no se despegue de su borde, sin salirse
  // ni meterse debajo de las flechas.
  function refit(x, width, anchor) {
    var w = width * (WIDE.width / SMALL.width) * SIZE_FACTOR;
    if (anchor === 'left') {
      var left = Math.max(SIDE_SAFE, Math.min(100, x - width / 2));
      w = Math.min(w, 100 - left);
      return { x: round1(left + w / 2), width: round1(w) };
    }
    if (anchor === 'right') {
      var right = Math.max(0, Math.min(100 - SIDE_SAFE, x + width / 2));
      w = Math.min(w, right);
      return { x: round1(right - w / 2), width: round1(w) };
    }
    var cx = Math.max(0, Math.min(100, x));
    w = Math.min(w, Math.max(width, 2 * Math.min(cx, 100 - cx)), 100);
    return { x: round1(cx), width: round1(w) };
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  // ── Cuenta atrás ────────────────────────────────────────────
  // banner.wide.countdown = { at, x, y, fontSize, color, bg, onEnd }: cuatro
  // cajas (días, horas, minutos y segundos) colocadas en el lienzo como el
  // botón (x e y del centro en %, fontSize = alto de las cifras en px del
  // lienzo). La fecha (at, ms), los colores y lo que pasa al llegar a cero
  // (onEnd: 'hide' quita el banner y su popup; 'zero' lo deja en 00) son de
  // las dos vistas. Una vista pequeña a medida guarda solo su colocación en
  // small.countdown = { x, y, fontSize }; si no, sale de la grande.
  var COUNTDOWN = { x: 80, y: 50, fontSize: 46, color: '#ffffff', bg: 'rgba(0, 0, 0, 0.45)', onEnd: 'hide' };
  var CD_MIN_AT = Date.UTC(2024, 0, 1);
  var CD_MAX_AT = Date.UTC(2100, 0, 1);
  var CD_UNITS = ['days', 'hours', 'minutes', 'seconds'];
  // Ancho aproximado de las cuatro cajas con sus huecos, en veces fontSize:
  // para que la derivada no se salga de la vista pequeña.
  var CD_WIDTH_FS = 7.2;

  // Solo colores CSS sencillos (#hex, rgb/rgba, hsl/hsla o un nombre): el
  // valor acaba dentro de un atributo style.
  function cssColor(value, fallback) {
    var v = String(value == null ? '' : value).trim();
    return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\)|[a-z]{3,20})$/i.test(v) ? v : fallback;
  }

  /** La cuenta atrás de la vista grande, limpia, o null si no hay (o no tiene fecha válida). */
  function countdownOf(wide) {
    var src = wide && typeof wide === 'object' ? wide.countdown : null;
    if (!src || typeof src !== 'object') return null;
    var at = Math.round(num(src.at, NaN));
    if (!isFinite(at) || at < CD_MIN_AT || at >= CD_MAX_AT) return null;
    return {
      at: at,
      x: clamp(num(src.x, COUNTDOWN.x), 0, 100),
      y: clamp(num(src.y, COUNTDOWN.y), 0, 100),
      fontSize: clamp(num(src.fontSize, COUNTDOWN.fontSize), 8, 200),
      color: cssColor(src.color, COUNTDOWN.color),
      // '' = sin cajas (solo las cifras).
      bg: src.bg === '' ? '' : cssColor(src.bg, COUNTDOWN.bg),
      onEnd: src.onEnd === 'zero' ? 'zero' : 'hide'
    };
  }

  // La de la grande, recolocada en la vista pequeña: algo menor (SIZE_FACTOR)
  // y sin salirse ni meterse bajo las flechas.
  function deriveSmallCountdown(cd) {
    if (!cd) return null;
    var out = copy(cd);
    out.fontSize = Math.max(8, Math.round(cd.fontSize * SIZE_FACTOR));
    var half = Math.min(50, (CD_WIDTH_FS * out.fontSize / SMALL.width) * 100 / 2);
    out.x = round1(clamp(cd.x, Math.min(50, SIDE_SAFE + half), Math.max(50, 100 - SIDE_SAFE - half)));
    return out;
  }

  /**
   * Lo que falta hasta `at`: { done, days, hours, minutes, seconds }. Los
   * segundos se cuentan hacia arriba (con 4,3 s se ve 5): nunca se ve
   * 00:00:00 antes de llegar.
   */
  function countdownParts(at, now) {
    var left = Math.max(0, num(at, 0) - num(now, 0));
    var secs = Math.ceil(left / 1000);
    return {
      done: secs <= 0,
      days: Math.floor(secs / 86400),
      hours: Math.floor((secs % 86400) / 3600),
      minutes: Math.floor((secs % 3600) / 60),
      seconds: secs % 60
    };
  }

  function pad2(n) {
    var s = String(Math.max(0, Math.floor(num(n, 0))));
    return s.length < 2 ? '0' + s : s;
  }

  /**
   * Las cuatro cajas (HTML) con lo que falta y sus nombres (`labels`:
   * { days, hours, minutes, seconds }, ya en el idioma de quien mira). Cada
   * cifra lleva data-cd-unit: el reloj de la app cambia solo su texto.
   */
  function countdownCellsHtml(parts, labels) {
    var L = labels || {};
    return CD_UNITS.map(function (unit) {
      return '<span class="bn-cd-cell bn-cd-' + unit + '"><b class="bn-cd-num" data-cd-unit="' + unit + '">' + pad2(parts[unit]) +
        '</b><span class="bn-cd-lbl">' + escText(L[unit] || '') + '</span></span>';
    }).join('');
  }

  /**
   * Estilos de la cuenta atrás (los mismos en la app y en el lienzo del
   * backoffice). `root` va dentro de :where() para no sumar especificidad:
   * el CSS a medida del banner los puede pisar con un selector sencillo.
   */
  function countdownCss(root) {
    var p = ':where(' + root + ')';
    return [
      p + ' .bn-countdown { position: absolute; transform: translate(-50%, -50%); display: flex; align-items: stretch; gap: calc(var(--fs) * 0.2 * var(--hm-u)); line-height: 1; font-variant-numeric: tabular-nums; white-space: nowrap; text-shadow: 0 3px 14px rgba(0, 0, 0, 0.6); pointer-events: none; }',
      p + ' .bn-cd-cell { display: flex; flex-direction: column; align-items: center; justify-content: center; box-sizing: border-box; min-width: calc(var(--fs) * 1.5 * var(--hm-u)); padding: calc(var(--fs) * 0.16 * var(--hm-u)) calc(var(--fs) * 0.18 * var(--hm-u)) calc(var(--fs) * 0.14 * var(--hm-u)); border-radius: calc(var(--fs) * 0.22 * var(--hm-u)); background: var(--cd-bg, transparent); }',
      p + ' .bn-cd-num { font-size: calc(var(--fs) * var(--hm-u)); font-weight: 800; }',
      p + ' .bn-cd-lbl { margin-top: calc(var(--fs) * 0.1 * var(--hm-u)); font-size: calc(var(--fs) * 0.26 * var(--hm-u)); font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; opacity: 0.85; }'
    ].join('\n');
  }

  /**
   * Vista pequeña a partir de la grande ({ overlays, ctaStyle, countdown } del doc).
   * → { overlays, ctaStyle, countdown }; el fondo es el de la grande.
   */
  function deriveSmall(wide) {
    var src = wide && typeof wide === 'object' ? wide : {};
    var overlays = (Array.isArray(src.overlays) ? src.overlays : []).filter(Boolean).map(function (layer) {
      var align = /^(left|center|right)$/.test(layer.align) ? layer.align : 'center';
      var box = refit(num(layer.x, 50), num(layer.width, 52), align);
      var out = copy(layer);
      out.x = box.x;
      out.width = box.width;
      out.y = num(layer.y, 50);
      out.fontSize = Math.max(8, Math.round(num(layer.fontSize, 28) * SIZE_FACTOR));
      return out;
    });
    var cta = src.ctaStyle && typeof src.ctaStyle === 'object' ? src.ctaStyle : {};
    var cx = num(cta.x, 12.5);
    var cbox = refit(cx, num(cta.width, 13), cx < 40 ? 'left' : cx > 60 ? 'right' : 'center');
    var ctaStyle = copy(cta);
    ctaStyle.x = cbox.x;
    ctaStyle.width = cbox.width;
    ctaStyle.y = num(cta.y, 82);
    ctaStyle.height = Math.max(24, Math.round(num(cta.height, 46) * SIZE_FACTOR));
    ctaStyle.fontSize = Math.max(8, Math.round(num(cta.fontSize, 17) * SIZE_FACTOR));
    ctaStyle.radius = Math.round(num(cta.radius, 10) * SIZE_FACTOR);
    return { overlays: overlays, ctaStyle: ctaStyle, countdown: deriveSmallCountdown(countdownOf(src)) };
  }

  // ¿Trae el popup su vista pequeña diseñada a mano en el backoffice?
  function hasCustomSmall(wide) {
    var small = wide && wide.small;
    return !!small && typeof small === 'object' && Array.isArray(small.overlays);
  }

  function hasImage(background) {
    return !!background && typeof background === 'object' && !!(background.es || background.en);
  }

  /**
   * Vista pequeña que toca pintar: la diseñada en el backoffice o, si no la
   * hay, la derivada de la grande. Sin imagen propia usa la de la grande.
   * → { background, overlays, ctaStyle, countdown, custom }
   */
  function smallOf(wide) {
    var src = wide && typeof wide === 'object' ? wide : {};
    if (hasCustomSmall(src)) {
      var derivedCta = null;
      var cd = countdownOf(src);
      var place = src.small.countdown;
      if (cd) {
        // Su colocación propia; sin ella, la derivada de la grande.
        if (place && typeof place === 'object') {
          cd.x = clamp(num(place.x, cd.x), 0, 100);
          cd.y = clamp(num(place.y, cd.y), 0, 100);
          cd.fontSize = clamp(num(place.fontSize, cd.fontSize), 8, 200);
        } else {
          cd = deriveSmallCountdown(cd);
        }
      }
      if (!(src.small.ctaStyle && typeof src.small.ctaStyle === 'object')) derivedCta = deriveSmall(src).ctaStyle;
      return {
        background: hasImage(src.small.background) ? src.small.background : (src.background || {}),
        overlays: src.small.overlays,
        ctaStyle: derivedCta || src.small.ctaStyle,
        countdown: cd,
        custom: true
      };
    }
    var derived = deriveSmall(src);
    return { background: src.background || {}, overlays: derived.overlays, ctaStyle: derived.ctaStyle, countdown: derived.countdown, custom: false };
  }

  // ── CSS a medida ────────────────────────────────────────────
  var CSS_MAX = 6000;
  var CSS_HOOKS = {
    view: { wide: 'bn-wide', small: 'bn-small' },
    bg: 'bn-bg',
    html: 'bn-html',
    text: 'bn-text',
    chip: 'bn-chip',
    button: 'bn-button',
    countdown: 'bn-countdown'
  };

  // Sin comillas ni cadenas, para contar llaves sin que engañe un content: "}".
  function withoutStrings(css) {
    return css.replace(/"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'/g, '""');
  }

  function bracesBalanced(css) {
    var depth = 0;
    var bare = withoutStrings(css);
    for (var i = 0; i < bare.length; i++) {
      if (bare[i] === '{') depth++;
      else if (bare[i] === '}' && --depth < 0) return false;
    }
    return depth === 0;
  }

  /**
   * Deja el CSS en lo que un banner puede necesitar: fuera comentarios,
   * HTML, @import / @font-face y todo lo que cargue algo de fuera (url(),
   * image-set()…): una promo no debe poder pedir recursos a otro servidor.
   * → '' si no queda nada.
   */
  function cleanCss(css) {
    var s = String(css == null ? '' : css).slice(0, CSS_MAX);
    s = s.replace(/\/\*[\s\S]*?\*\//g, ' ');
    s = s.replace(/\\/g, '');   // sin escapes: u\72l( no puede disfrazar un url(
    s = s.replace(/</g, '');
    s = s.replace(/@(import|charset|namespace)\b[^;{}]*;?/gi, '');
    s = s.replace(/@(font-face|page|document|property)\b[^{}]*\{[^{}]*\}/gi, '');
    s = s.replace(/url\s*\(\s*(?:"[^"]*"|'[^']*'|[^)]*)\)/gi, 'none');
    s = s.replace(/\b(-webkit-image-set|image-set|image|src|element|cross-fade|paint)\s*\(/gi, 'x(');
    s = s.replace(/\bexpression\s*\(/gi, 'x(').replace(/(-moz-binding|behavior)\s*:/gi, 'x-off:');
    // Y ninguna dirección suelta entre comillas (lo que quedara dentro de
    // una función ya anulada): ni http(s)://, ni //servidor, ni data:.
    s = s.replace(/(["'])\s*(?:[a-z][a-z0-9+.-]*:)?\/\/[^"'\n]*\1/gi, '""').replace(/(["'])\s*data:[^"'\n]*\1/gi, '""');
    return s.trim();
  }

  // Qué le pasa al CSS escrito, para avisar en el editor ('' = nada).
  function cssProblem(css) {
    var raw = String(css == null ? '' : css);
    if (raw.length > CSS_MAX) return 'too_long';
    if (!bracesBalanced(cleanCss(raw))) return 'unbalanced';
    return '';
  }

  // Los @keyframes no pueden vivir dentro de @scope: se sacan fuera.
  function splitKeyframes(css) {
    var frames = [];
    var rest = '';
    var from = 0;
    var re = /@(?:-webkit-)?keyframes\s+[\w-]+\s*\{/gi;
    var match;
    while ((match = re.exec(css))) {
      var depth = 1;
      var i = re.lastIndex;
      while (i < css.length && depth > 0) {
        if (css[i] === '{') depth++;
        else if (css[i] === '}') depth--;
        i++;
      }
      if (depth !== 0) return null;
      rest += css.slice(from, match.index);
      frames.push(css.slice(match.index, i));
      from = i;
      re.lastIndex = i;
    }
    return { frames: frames, rest: rest + css.slice(from) };
  }

  /**
   * Hoja de estilos lista para inyectar: el CSS limpio, encerrado en el
   * elemento que casa con `scopeSelector` (un banner concreto). Con las
   * llaves mal cerradas no se aplica nada: podría escaparse del @scope.
   */
  function scopedCss(css, scopeSelector) {
    var clean = cleanCss(css);
    if (!clean || !scopeSelector || !bracesBalanced(clean)) return '';
    var parts = splitKeyframes(clean);
    if (!parts || !bracesBalanced(parts.rest)) return '';
    var body = parts.rest.trim();
    return parts.frames.join('\n') + (parts.frames.length && body ? '\n' : '') +
      (body ? '@scope (' + scopeSelector + ') {\n' + body + '\n}' : '');
  }

  // ── HTML a medida ───────────────────────────────────────────
  // banner.wide.html = { es, en }: marcado propio del banner, para lo que no
  // cabe en «textos + botón» (formas, listas, una imagen recortada…). Va en
  // una capa a todo el lienzo (.bn-html), encima del fondo y debajo de los
  // textos y el botón del editor, en las dos vistas; se le da forma con el
  // CSS a medida. cleanHtml() NO deja pasar nada de lo escrito tal cual:
  // lo trocea y vuelve a escribir solo las etiquetas y atributos de la lista
  // (sin scripts, sin style="", sin on*, sin enlaces, y con las imágenes solo
  // de nuestro almacenamiento), con el texto escapado y todo bien cerrado,
  // para que no pueda tragarse los textos y el botón que van detrás.
  var HTML_MAX = 8000;
  var HTML_DEPTH = 24;
  var HTML_TAGS = ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'strong', 'b', 'em', 'i', 'u', 's', 'small', 'mark', 'sup', 'sub',
    'br', 'ul', 'ol', 'li', 'img', 'svg', 'g', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon'];
  var HTML_ATTRS = {
    all: ['class', 'aria-hidden', 'aria-label', 'role', 'lang', 'dir'],
    img: ['src', 'alt'],
    svg: ['viewbox', 'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points',
      'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
      'opacity', 'fill-opacity', 'stroke-opacity', 'fill-rule', 'clip-rule', 'transform', 'preserveaspectratio']
  };
  // Las imágenes, solo de nuestro almacenamiento (las que sube el backoffice).
  var HTML_IMG_SRC = /^https:\/\/storage\.tikcontrol\.live\/[A-Za-z0-9._~\-\/%]+(?:\?[A-Za-z0-9._~\-=&%]*)?$/;
  var HTML_VOID = { br: 1, img: 1 };
  var SVG_TAGS = { svg: 1, g: 1, path: 1, circle: 1, ellipse: 1, rect: 1, line: 1, polyline: 1, polygon: 1 };
  var SVG_SHAPES = { path: 1, circle: 1, ellipse: 1, rect: 1, line: 1, polyline: 1, polygon: 1 };
  // Estas se van con todo lo que llevan dentro (el resto de etiquetas no
  // admitidas se quitan, pero su texto se queda).
  var HTML_DROP = { script: 1, style: 1, iframe: 1, frame: 1, frameset: 1, object: 1, embed: 1, applet: 1, noscript: 1, noembed: 1,
    noframes: 1, template: 1, textarea: 1, select: 1, title: 1, head: 1, xmp: 1, plaintext: 1, math: 1, foreignobject: 1,
    video: 1, audio: 1, canvas: 1 };
  // Atributos que se quitan sin avisar (ruido de un SVG pegado tal cual).
  var HTML_QUIET = { xmlns: 1, 'xmlns:xlink': 1, version: 1, 'xml:space': 1 };
  var HTML_TAG_SET = {};
  HTML_TAGS.forEach(function (tag) { HTML_TAG_SET[tag] = 1; });

  function setOf(list) {
    var out = {};
    list.forEach(function (name) { out[name] = 1; });
    return out;
  }
  var ATTR_ALL = setOf(HTML_ATTRS.all);
  var ATTR_IMG = setOf(HTML_ATTRS.all.concat(HTML_ATTRS.img));
  var ATTR_SVG = setOf(HTML_ATTRS.all.concat(HTML_ATTRS.svg));

  function escAttr(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Texto entre etiquetas: se conservan las entidades bien escritas (&amp;,
  // &nbsp;, &#8364;…); cualquier otro & y todo < > se escapan.
  function escText(text) {
    return String(text)
      .replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]{1,31}|#\d{1,7}|#[xX][0-9a-fA-F]{1,6});)/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Valor de un atributo ya admitido → el que se escribe, o null si no vale.
  function htmlAttrValue(name, raw) {
    var v = String(raw).replace(/\s+/g, ' ').trim();
    if (name === 'class') {
      // Clases propias. Las hm-* son de la app (su carrusel): no se prestan.
      var tokens = v.split(' ').filter(function (token) {
        return /^[A-Za-z_][\w-]{0,47}$/.test(token) && !/^hm-/i.test(token);
      }).slice(0, 12);
      return tokens.length ? tokens.join(' ') : null;
    }
    if (name === 'src') return HTML_IMG_SRC.test(v) ? v : null;
    if (name === 'alt' || name === 'aria-label') return v.slice(0, 160);
    if (name === 'aria-hidden') return /^(true|false)$/.test(v) ? v : null;
    if (name === 'role') return /^[a-z]{1,20}$/.test(v) ? v : null;
    if (name === 'lang') return /^[A-Za-z-]{2,12}$/.test(v) ? v : null;
    if (name === 'dir') return /^(ltr|rtl|auto)$/.test(v) ? v : null;
    // Geometría y pintura de SVG: números, colores sencillos y funciones como
    // rotate(…) o rgb(…). Sin «:» no cabe ningún esquema; sin url(), ninguna referencia.
    if (v.length > (name === 'd' || name === 'points' ? 6000 : 300)) return null;
    if (!/^[A-Za-z0-9 .,%#()\-+\/]*$/.test(v) || /url\s*\(|expression/i.test(v)) return null;
    return v;
  }

  function htmlAttrs(tag, raw, drop) {
    var allowed = tag === 'img' ? ATTR_IMG : SVG_TAGS[tag] ? ATTR_SVG : ATTR_ALL;
    var out = '';
    var seen = {};
    var re = /([^\s"'<>\/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
    var match;
    while ((match = re.exec(raw))) {
      var name = match[1].toLowerCase();
      if (seen[name] || HTML_QUIET[name]) continue;
      if (!allowed[name]) { drop(name + '='); continue; }
      var value = htmlAttrValue(name, match[2] != null ? match[2] : match[3] != null ? match[3] : match[4] != null ? match[4] : '');
      if (value === null) { drop(name + '='); continue; }
      seen[name] = 1;
      out += ' ' + name + '="' + escAttr(value) + '"';
    }
    return out;
  }

  /**
   * HTML a medida → { html, dropped }: el marcado que se puede pintar y lo
   * que se ha quitado (etiquetas `<x>` y atributos `x=`), para avisar en el
   * editor. Lo que sale está siempre bien cerrado.
   */
  function cleanHtmlReport(input) {
    var s = String(input == null ? '' : input).slice(0, HTML_MAX);
    var lower = s.toLowerCase();
    var out = '';
    var stack = [];
    var dropped = [];
    function drop(what) { if (dropped.indexOf(what) < 0 && dropped.length < 20) dropped.push(what); }
    var tagRe = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^'">])*)>/y;
    var i = 0;
    while (i < s.length) {
      var lt = s.indexOf('<', i);
      if (lt < 0) { out += escText(s.slice(i)); break; }
      out += escText(s.slice(i, lt));
      if (s.startsWith('<!--', lt)) {
        var endComment = s.indexOf('-->', lt + 4);
        i = endComment < 0 ? s.length : endComment + 3;
        continue;
      }
      tagRe.lastIndex = lt;
      var match = tagRe.exec(s);
      if (!match) {
        // «<!doctype…», «<?xml…» o un cierre mal escrito: fuera hasta su «>».
        // Un «<» suelto (2 < 3) es texto.
        if (/[!?\/]/.test(s.charAt(lt + 1))) {
          var gt = s.indexOf('>', lt);
          i = gt < 0 ? s.length : gt + 1;
        } else {
          out += '&lt;';
          i = lt + 1;
        }
        continue;
      }
      i = lt + match[0].length;
      var name = match[2].toLowerCase();
      var closing = match[1] === '/';
      if (HTML_DROP[name]) {
        drop('<' + name + '>');
        if (!closing) {
          var close = lower.indexOf('</' + name, i);
          var closeGt = close < 0 ? -1 : s.indexOf('>', close);
          i = closeGt < 0 ? s.length : closeGt + 1;
        }
        continue;
      }
      if (!HTML_TAG_SET[name]) { drop('<' + name + '>'); continue; }
      if (closing) {
        var at = stack.lastIndexOf(name);
        if (at >= 0) while (stack.length > at) out += '</' + stack.pop() + '>';
        continue;
      }
      var attrs = htmlAttrs(name, match[3], drop);
      if (name === 'img' && attrs.indexOf(' src="') < 0) { drop('<img> sin src de storage.tikcontrol.live'); continue; }
      if (HTML_VOID[name]) { out += '<' + name + attrs + '>'; continue; }
      // Las formas de SVG no llevan nada dentro: se cierran aquí mismo.
      if (SVG_SHAPES[name]) { out += '<' + name + attrs + '></' + name + '>'; continue; }
      if (stack.length >= HTML_DEPTH) { drop('más de ' + HTML_DEPTH + ' niveles'); continue; }
      out += '<' + name + attrs + '>';
      stack.push(name);
    }
    while (stack.length) out += '</' + stack.pop() + '>';
    return { html: out.trim(), dropped: dropped };
  }

  function cleanHtml(input) {
    return cleanHtmlReport(input).html;
  }

  // Qué le pasa al HTML escrito, para avisar en el editor ('' = nada).
  function htmlProblem(input) {
    return String(input == null ? '' : input).length > HTML_MAX ? 'too_long' : '';
  }

  // ── Nombres que se pueden usar (lo que enseña el editor del backoffice) ──
  // Son los que pintan promos.js y el lienzo del editor: si se renombra algo
  // allí, se cambia aquí (tests/inicio-banner-vistas-test.js lo comprueba).
  var REFERENCE = {
    structure: [
      ':scope                    el banner entero',
      '└ .bn-wide  /  .bn-small   cada vista (lienzo de ' + WIDE.width + ' × ' + WIDE.height + '  /  ' + SMALL.width + ' × ' + SMALL.height + ')',
      '   ├ .bn-bg                la imagen de fondo',
      '   ├ .bn-html              tu HTML (capa a todo el lienzo)',
      '   ├ .bn-text .bn-text-1   cada texto del editor, por orden (.bn-text-2, .bn-text-3…)',
      '   │   └ .bn-chip          su pastilla, si tiene color de fondo',
      '   ├ .bn-countdown         la cuenta atrás, si la tiene',
      '   │   └ .bn-cd-cell       cada caja (.bn-cd-days, .bn-cd-hours…), con .bn-cd-num y .bn-cd-lbl',
      '   └ .bn-button            el botón'
    ],
    classes: [
      { name: ':scope', what: 'El banner entero, con sus dos vistas.' },
      { name: '.bn-wide', what: 'La vista grande (lienzo de ' + WIDE.width + ' × ' + WIDE.height + ' px).' },
      { name: '.bn-small', what: 'La vista pequeña (lienzo de ' + SMALL.width + ' × ' + SMALL.height + ' px): la que se ve con el banner a menos de ' + SMALL_BELOW + ' px de ancho.' },
      { name: '.bn-bg', what: 'La imagen de fondo.' },
      { name: '.bn-html', what: 'La capa con tu HTML: ocupa todo el lienzo, encima del fondo y debajo de los textos y el botón. Dentro, 1em = 16 px del lienzo.' },
      { name: '.bn-text', what: 'Todos los textos del editor.' },
      { name: '.bn-text-1, .bn-text-2…', what: 'Un texto concreto, por su orden en la barra del editor.' },
      { name: '.bn-chip', what: 'La pastilla de un texto (cuando tiene color de fondo).' },
      { name: '.bn-button', what: 'El botón.' },
      { name: '.bn-countdown', what: 'La cuenta atrás (las cuatro cajas juntas).' },
      { name: '.bn-cd-cell', what: 'Cada caja de la cuenta atrás; una concreta: .bn-cd-days, .bn-cd-hours, .bn-cd-minutes, .bn-cd-seconds.' },
      { name: '.bn-cd-num, .bn-cd-lbl', what: 'La cifra y el nombre (días, horas…) de cada caja.' }
    ],
    variables: [
      { name: '--hm-u', what: '1 px del lienzo, ya escalado al tamaño real del banner. Para un tamaño que encoja con él: calc(20 * var(--hm-u)).' },
      { name: '--fs', what: 'En .bn-text y .bn-button: su tamaño de letra, en px del lienzo (lo pone el editor).' },
      { name: '--h', what: 'En .bn-button: su alto mínimo, en px del lienzo.' },
      { name: '--r', what: 'En .bn-button: el radio de sus esquinas, en px del lienzo.' },
      { name: '--cd-bg', what: 'En .bn-countdown: el fondo de las cajas (vacío = sin cajas). Su --fs es el alto de las cifras.' }
    ],
    tags: HTML_TAGS,
    attrs: HTML_ATTRS
  };

  var api = {
    WIDE: WIDE,
    SMALL: SMALL,
    SMALL_BELOW: SMALL_BELOW,
    deriveSmall: deriveSmall,
    hasCustomSmall: hasCustomSmall,
    smallOf: smallOf,
    COUNTDOWN: COUNTDOWN,
    countdownOf: countdownOf,
    countdownParts: countdownParts,
    countdownCellsHtml: countdownCellsHtml,
    countdownCss: countdownCss,
    pad2: pad2,
    CSS_MAX: CSS_MAX,
    CSS_HOOKS: CSS_HOOKS,
    cleanCss: cleanCss,
    cssProblem: cssProblem,
    scopedCss: scopedCss,
    HTML_MAX: HTML_MAX,
    HTML_TAGS: HTML_TAGS,
    HTML_ATTRS: HTML_ATTRS,
    cleanHtml: cleanHtml,
    cleanHtmlReport: cleanHtmlReport,
    htmlProblem: htmlProblem,
    REFERENCE: REFERENCE
  };

  root.TCBannerViews = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
